/**
 * AdsTrendChart — daily metric trends for the selected range.
 *
 * ApexCharts (already bundled) themed to the marketing surface's dark
 * terminal palette. The visible series are driven by the KPI tiles above the
 * chart (togglable): cost / conv. value share the left currency axis,
 * clicks / conversions share the right count axis, and impressions (orders of
 * magnitude larger) rides its own hidden axis so it shows shape without
 * flattening everything else.
 */

import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { chartTheme } from './chartTheme';

export interface TrendPoint {
  date: string;
  cost: number;
  clicks: number;
  conversions: number;
  impressions: number;
  conversionsValue: number;
}

export type TrendMetric = 'cost' | 'impressions' | 'clicks' | 'conversions' | 'conversionsValue';

export const TREND_METRICS: Record<TrendMetric, { label: string; color: string; kind: 'currency' | 'count' | 'big' }> = {
  cost:             { label: 'cost',        color: '#4ec07c', kind: 'currency' },
  conversionsValue: { label: 'conv. value', color: '#3fb8a8', kind: 'currency' },
  clicks:           { label: 'clicks',      color: '#5ea8d4', kind: 'count' },
  conversions:      { label: 'conv.',       color: '#d4a04e', kind: 'count' },
  impressions:      { label: 'impr.',       color: '#9a8fd4', kind: 'big' },
};

const DEFAULT_METRICS: TrendMetric[] = ['cost', 'conversions'];

export const AdsTrendChart: React.FC<{ points: TrendPoint[]; currency?: string; metrics?: TrendMetric[] }> = ({ points, currency, metrics }) => {
  const ct = chartTheme();
  const active = useMemo<TrendMetric[]>(() => {
    const m = (metrics && metrics.length ? metrics : DEFAULT_METRICS).filter((k) => TREND_METRICS[k]);
    return m.length ? m : DEFAULT_METRICS;
  }, [metrics]);

  const sorted = useMemo(
    () => [...points].sort((a, b) => a.date.localeCompare(b.date)),
    [points],
  );

  const series = useMemo(() => active.map((key) => ({
    name: TREND_METRICS[key].label,
    // cost keeps its signature area fill; everything else is a line.
    type: key === 'cost' ? 'area' : 'line',
    data: sorted.map((p) => Math.round(((p as any)[key] || 0) * 100) / 100),
  })), [active, sorted]);

  // Axis sharing: the FIRST series of each kind owns a visible axis; later
  // series of the same kind bind to it via seriesName. 'big' (impressions)
  // gets its own hidden axis so its scale doesn't flatten the others.
  const yaxis = useMemo(() => {
    const firstOfKind: Partial<Record<'currency' | 'count' | 'big', string>> = {};
    return active.map((key) => {
      const def = TREND_METRICS[key];
      const owner = firstOfKind[def.kind];
      if (owner) {
        return { seriesName: owner, show: false, labels: { formatter: (v: number) => String(v) } };
      }
      firstOfKind[def.kind] = def.label;
      if (def.kind === 'big') {
        return { seriesName: def.label, show: false, opposite: true, labels: { formatter: (v: number) => String(v) } };
      }
      return {
        seriesName: def.label,
        opposite: def.kind === 'count',
        title: { text: def.kind === 'currency' ? `cost (${currency || 'cur'})` : 'count', style: { color: ct.axis, fontSize: '10px' } },
        labels: { style: { colors: ct.axis, fontSize: '10px' }, formatter: (v: number) => Math.round(v).toLocaleString() },
      };
    });
  }, [active, currency]);

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: 240,
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      animations: { enabled: false },
    },
    theme: { mode: ct.mode },
    colors: active.map((k) => TREND_METRICS[k].color),
    stroke: { width: active.map(() => 2), curve: 'smooth' },
    fill: {
      type: active.map((k) => (k === 'cost' ? 'gradient' : 'solid')),
      gradient: { opacityFrom: 0.35, opacityTo: 0.02 },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: ct.grid, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    markers: { size: 0, hover: { size: 4 } },
    xaxis: {
      categories: sorted.map((p) => p.date),
      tickAmount: Math.min(12, Math.max(1, sorted.length)),
      labels: { style: { colors: ct.axis, fontSize: '10px' }, rotate: -45, hideOverlappingLabels: true, trim: true },
      axisBorder: { color: ct.grid },
      axisTicks: { color: ct.grid },
    },
    yaxis: yaxis as any,
    legend: {
      labels: { colors: ct.legend },
      markers: { width: 8, height: 8 } as any,
    },
    tooltip: {
      theme: ct.mode,
      shared: true,
      intersect: false,
      y: active.map((k) => ({
        formatter: (v: number) => {
          if (v == null) return '—';
          if (TREND_METRICS[k].kind === 'currency') {
            // Never GUESS a currency: an AUD account briefly rendering US$ is
            // worse than a bare number. Matches the page's no-guess rule —
            // currency comes from customer_summary or not at all.
            return currency
              ? new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v)
              : new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
          }
          return Number(v).toLocaleString();
        },
      })),
    },
  }), [active, sorted, yaxis, currency]);

  if (!sorted.length) {
    return <div className="gads-empty">no daily data for this range</div>;
  }
  return <Chart options={options} series={series as any} type="line" height={240} />;
};

export default AdsTrendChart;
